import React from 'react'
import { useSelector } from 'hooks/use-redux'
import { isDeviceTypePhone } from '@disco/state-management/selectors/app'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import ContactUsLink from 'components/ui/contact-us-link'
import Heading from 'components/ui/heading'
import Icon from 'components/ui/icon'
import List from 'components/ui/list'
import ListItemWithIcon from 'components/ui/list-item-with-icon'
import ModalWindow from 'components/ui/modal-window'
import Stack from 'components/ui/stack'
import Text from 'components/ui/text'

type ChargebeeErrorCode =
  | 'payment_processing_failed'
  | 'payment_method_verification_failed'

type Props = {
  error?: { body?: { api_error_code?: ChargebeeErrorCode } }
  onRequestClose: (...args: Array<any>) => any
}

const ChargebeeFormErrModal = ({
  error,
  onRequestClose,
}: Props): React.ReactNode => {
  const isPhone = useSelector(isDeviceTypePhone)
  const ModalButtonContainer = isPhone ? React.Fragment : ButtonContainer
  const modalButtonContainerProps = isPhone
    ? {}
    : {
        alignRight: true,
      }

  const title = 'Your payment could not be processed:'

  let errorText = 'Something went wrong'

  if (error?.body?.api_error_code) {
    errorText = getErrorMessageFromChargebeeErrorCode(error.body.api_error_code)
  }

  return (
    <ModalWindow
      header={<Heading size={700}>Sorry!</Heading>}
      onClose={onRequestClose}
      isOpen
      hiddenCloseButton
      width="small"
    >
      <Stack space="small">
        <Text type="paragraph">
          <Heading size="500">{title}</Heading>
          <List>
            <ListItemWithIcon
              iconNode={<Icon type="warning" color="#fd7d69" />}
            >
              {errorText}
            </ListItemWithIcon>
          </List>
          Please check your payment details or contact your financial
          institution.
          <br /> If you continue having issues, please{' '}
          <ContactUsLink className="text-link">contact us.</ContactUsLink>
        </Text>

        <ModalButtonContainer {...modalButtonContainerProps}>
          <Button
            caption="Ok"
            appearance="primary"
            onClick={onRequestClose}
            fullWidth={isPhone}
            textCenter={isPhone}
          />
        </ModalButtonContainer>
      </Stack>
    </ModalWindow>
  )
}

export default ChargebeeFormErrModal

function getErrorMessageFromChargebeeErrorCode(
  chargebeeErrorCode: ChargebeeErrorCode,
) {
  switch (chargebeeErrorCode) {
    case 'payment_processing_failed':
      return 'Card was declined'
    case 'payment_method_verification_failed':
      return 'Card details were invalid'
    default:
      return 'Something went wrong'
  }
}
